import { useQueryClient } from '@tanstack/react-query'
import { HeartPulse } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  usePostAgentsHealthCheck,
  getGetAgentsQueryKey,
  getGetAgentsByIdQueryKey,
} from '@/api/control/control-plane/control-plane'
import type { AgentDto } from '@/api/control/missionImpactAPI.schemas'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { getHealthCheckStage, isHealthCheckRunning } from '@/lib/agentHealth'

interface ConfirmAgentHealthButtonProps {
  orgId: string
  agent: AgentDto
}

/**
 * Starts a health check of an active agent ("Confirm health"). The agent is sent a quick query for
 * each of its targets; the result appears next to the agent's health as soon as it replies, or
 * after 3 minutes without a reply (when the agent is marked offline).
 */
export function ConfirmAgentHealthButton({ orgId, agent }: ConfirmAgentHealthButtonProps) {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const { handleApiError } = useError()

  const { mutate: startHealthCheck, isPending } = usePostAgentsHealthCheck({
    mutation: {
      onSuccess: () => {
        toast.success(t.agents.healthCheck.started)
        void queryClient.invalidateQueries({ queryKey: getGetAgentsQueryKey(orgId) })
        void queryClient.invalidateQueries({ queryKey: getGetAgentsByIdQueryKey(orgId, agent.id) })
      },
      onError: (error) => {
        handleApiError(error, { context: 'ConfirmAgentHealthButton.startHealthCheck' })
      },
    },
  })

  if (agent.status !== 'Active') {
    return null
  }

  const running = isHealthCheckRunning(getHealthCheckStage(agent, Date.now()))

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => { startHealthCheck({ orgId, agentId: agent.id }) }}
          disabled={isPending || running}
          aria-label={t.agents.healthCheck.confirm}
        >
          <HeartPulse className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t.agents.healthCheck.confirm}</TooltipContent>
    </Tooltip>
  )
}
